import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, X, Table2 } from 'lucide-react';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import ChartWidget from './ChartWidget';
import ChartConfigDialog from './ChartConfigDialog';
import PivotConfigDialog from './PivotConfigDialog';
import { Card, CardContent } from '@/components/ui/card';

export default function PCERDashboard({ 
  reportData, 
  widgets: initialWidgets = [], 
  layouts: initialLayouts = [],
  onWidgetsChange,
  onLayoutsChange 
}) {
  const [widgets, setWidgets] = useState(initialWidgets);
  const [layouts, setLayouts] = useState(initialLayouts);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [isPivotConfigOpen, setIsPivotConfigOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);
  const [maximizedWidget, setMaximizedWidget] = useState(null);
  const [containerWidth, setContainerWidth] = useState(1200);
  const containerRef = useRef(null);

  // Notify parent when widgets change
  useEffect(() => {
    if (onWidgetsChange) {
      onWidgetsChange(widgets);
    }
  }, [widgets, onWidgetsChange]);

  // Notify parent when layouts change
  useEffect(() => {
    if (onLayoutsChange) {
      onLayoutsChange(layouts);
    }
  }, [layouts, onLayoutsChange]);

  useEffect(() => {
    if (containerRef.current) {
      const updateWidth = () => {
        setContainerWidth(containerRef.current.offsetWidth);
      };
      updateWidth();
      
      const resizeObserver = new ResizeObserver(updateWidth);
      resizeObserver.observe(containerRef.current);
      
      return () => resizeObserver.disconnect();
    }
  }, []);

  const addWidget = (widgetConfig) => {
    // Handle advanced pivot table with separate dialog
    if (widgetConfig.type === 'advancedPivot') {
      setIsConfigOpen(false);
      setIsPivotConfigOpen(true);
      return;
    }
    
    const newWidget = {
      id: `widget-${Date.now()}`,
      ...widgetConfig
    };

    const newLayout = {
      i: newWidget.id,
      x: (layouts.length * 2) % 12,
      y: Math.floor(layouts.length / 6) * 4,
      w: 6,
      h: 4,
      minW: 3,
      minH: 3
    };

    setWidgets([...widgets, newWidget]);
    setLayouts([...layouts, newLayout]);
    setIsConfigOpen(false);
    setEditingWidget(null);
  };

  const addPivotWidget = (widgetConfig) => {
    const newWidget = {
      id: `widget-${Date.now()}`,
      ...widgetConfig,
      type: 'advancedPivot'
    };

    const newLayout = {
      i: newWidget.id,
      x: (layouts.length * 2) % 12,
      y: Math.floor(layouts.length / 6) * 4,
      w: 6,
      h: 4,
      minW: 3,
      minH: 3
    };

    setWidgets([...widgets, newWidget]);
    setLayouts([...layouts, newLayout]);
    setIsPivotConfigOpen(false);
    setEditingWidget(null);
  };

  const updateWidget = (widgetConfig) => {
    setWidgets(widgets.map(w => 
      w.id === editingWidget.id 
        ? { ...w, ...widgetConfig }
        : w
    ));
    setIsConfigOpen(false);
    setEditingWidget(null);
  };

  const updatePivotWidget = (widgetConfig) => {
    setWidgets(widgets.map(w => 
      w.id === editingWidget.id 
        ? { ...w, ...widgetConfig, type: 'advancedPivot' }
        : w
    ));
    setIsPivotConfigOpen(false);
    setEditingWidget(null);
  };

  const removeWidget = (widgetId) => {
    setWidgets(widgets.filter(w => w.id !== widgetId));
    setLayouts(layouts.filter(l => l.i !== widgetId));
  };

  const openEditDialog = (widget) => {
    setEditingWidget(widget);
    if (widget.type === 'advancedPivot') {
      setIsPivotConfigOpen(true);
    } else {
      setIsConfigOpen(true);
    }
  };

  const handleLayoutChange = (newLayout) => {
    setLayouts(newLayout);
  };

  if (maximizedWidget) {
    const widget = widgets.find(w => w.id === maximizedWidget);
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between p-4 border-b bg-white">
          <h2 className="text-xl font-semibold">{widget.title}</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMaximizedWidget(null)}
          >
            <X className="w-4 h-4 mr-2" />
            Close
          </Button>
        </div>
        <div className="flex-1 p-4">
          <ChartWidget
            widget={widget}
            reportData={reportData}
            onRemove={() => {}}
            onEdit={() => openEditDialog(widget)}
            onMaximize={() => {}}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-4 border-b bg-white">
        <h2 className="text-lg font-semibold">Dashboard</h2>
        <div className="flex gap-2">
          <Button
            onClick={() => {
              setEditingWidget(null);
              setIsConfigOpen(true);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Chart
          </Button>
        </div>
      </div>

      {/* Dashboard Grid */}
      <div ref={containerRef} className="flex-1 overflow-auto p-4 bg-gray-50">
        {widgets.length === 0 ? (
          <Card className="h-full flex items-center justify-center">
            <CardContent className="text-center">
              <p className="text-gray-600 mb-4">No charts added yet</p>
              <Button
                onClick={() => {
                  setEditingWidget(null);
                  setIsConfigOpen(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Chart
              </Button>
            </CardContent>
          </Card>
        ) : (
          <GridLayout
            className="layout"
            layout={layouts}
            cols={12}
            rowHeight={60}
            width={containerWidth}
            onLayoutChange={handleLayoutChange}
            draggableHandle=".drag-handle"
            containerPadding={[0, 0]}
            margin={[16, 16]}
            isResizable={true}
            isDraggable={true}
          >
            {widgets.map(widget => (
              <div key={widget.id} className="drag-handle">
                <ChartWidget
                  widget={widget}
                  reportData={reportData}
                  onRemove={() => removeWidget(widget.id)}
                  onEdit={() => openEditDialog(widget)}
                  onMaximize={() => setMaximizedWidget(widget.id)}
                />
              </div>
            ))}
          </GridLayout>
        )}
      </div>

      {/* Chart Config Dialog */}
      <ChartConfigDialog
        isOpen={isConfigOpen}
        onClose={() => {
          setIsConfigOpen(false);
          setEditingWidget(null);
        }}
        onSave={editingWidget ? updateWidget : addWidget}
        editingWidget={editingWidget}
      />

      {/* Pivot Config Dialog */}
      <PivotConfigDialog
        isOpen={isPivotConfigOpen}
        onClose={() => {
          setIsPivotConfigOpen(false);
          setEditingWidget(null);
        }}
        onSave={editingWidget ? updatePivotWidget : addPivotWidget}
        editingWidget={editingWidget}
      />
    </div>
  );
}
